import { DirectorDocumentState } from "../steps/BusinessDocuments"

export const extractRequiredDocs = (data: any) => {
    return data.map((item) => ({
        id: item.businessDocument.id,
        document: item.businessDocument.document
    }))
}

export const isDocumentRequired = (documentTitle: string, requiredDocs: any) => {
    return requiredDocs.some(item => item.document?.toLowerCase() === documentTitle.toLowerCase())
}

const updateDirectorDocumentState = (
    directorId: number,
    documentId: string,
    updates: Partial<DirectorDocumentState>,
    setDirectorDocumentStates
  ) => {
    setDirectorDocumentStates((prevStates) => ({
      ...prevStates,
      [directorId]: {
        ...(prevStates[directorId] || {}),
        [documentId]: {
          ...(prevStates[directorId]?.[documentId] || {
            file: null,
            fileUrl: null,
            uploading: false,
            error: null,
          }),
          ...updates,
        },
      },
    }));
  };

 export const handleDirectorFileSelect = (
    directorId: number,
    documentId: string,
    uploadPath: string,
    setDirectorDocumentStates: any,
    directorFileUploadLogic: any,
  ) => async (file: File) => {
    updateDirectorDocumentState(directorId, documentId, {
      file,
      uploading: true,
      error: null,
    }, setDirectorDocumentStates);

    try {
      const uploadedUrl = await directorFileUploadLogic.uploadFile(file, uploadPath);
      updateDirectorDocumentState(directorId, documentId, {
        uploading: false,
        fileUrl: uploadedUrl,
      }, setDirectorDocumentStates);
    } catch (error) {
      updateDirectorDocumentState(directorId, documentId, {
        uploading: false,
        error: 'Failed to upload file',
      }, setDirectorDocumentStates);
    }
  };

  export const handleDirectorFileDelete = (
    directorId: number,
    documentId: string,
    setDirectorDocumentStates
  ) => () => {
    updateDirectorDocumentState(directorId, documentId, {
      file: null,
      fileUrl: null,
      uploading: false,
      error: null,
    }, setDirectorDocumentStates);
  };

  export const getDirectorDocumentState = (
    directorId: number,
    documentId: string,
    directorDocumentStates
  ): DirectorDocumentState => {
    return (
      directorDocumentStates[directorId]?.[documentId] || {
        file: null,
        fileUrl: null,
        uploading: false,
        error: null,
      }
    );
  };

  export const extractFileNameFromUrl = (
    url: string, 
  ): string | null => {
    try {
      const decodedUrl = decodeURIComponent(url);
      const match = decodedUrl.match(/\/([^/?]+)(\?|$)/)
      return match ? match[1] : null;
    } catch (error) {
      console.error('Error extracting file name:', error);
      return null;
    }
  };

  export const getMostRecentUploadedDocs = (uploadedDocuments: any[], requiredDocuments: any[]) => {
    const recentDocsMap = new Map();
    
    const requiredDocIds = requiredDocuments?.map(doc => doc.id) || [];
  
    uploadedDocuments.forEach((doc) => {
      if (requiredDocIds.includes(doc.businessDocument.id)) {
        const { createdAt, businessDocument, directorName } = doc;
        const uniqueKey = `${businessDocument.id}-${directorName || ''}`;
      
        const storedDoc = recentDocsMap.get(uniqueKey);
        const parsedDate = new Date(createdAt);
      
        if (!storedDoc || parsedDate > storedDoc.storedDate) {
          recentDocsMap.set(uniqueKey, {
            doc,
            storedDate: parsedDate
          });
        }
      }
    });
    
    const mostRecentDocs = Array.from(recentDocsMap.values()).map((item) => item.doc);
  
    const previousDocsForCurrentBizType: any[] = [];
    const missingDocuments: any[] = [];
    
    requiredDocuments?.forEach((requiredDoc) => {
      const exists = mostRecentDocs.some(
        uploadedDoc => uploadedDoc.businessDocument.id === requiredDoc.id
      );
  
      if (exists) {
        previousDocsForCurrentBizType.push(
          mostRecentDocs.find(uploadedDoc => uploadedDoc.businessDocument.id === requiredDoc.id)
        );
      } else {
        missingDocuments.push({
          businessDocument: {
            id: requiredDoc.id,
            document: requiredDoc.document,
            description: requiredDoc.description || requiredDoc.document,
            type: "document" 
          },
          documentApprovalStatus: "NOT UPLOADED"
        });
      }
    });
    
    return [...previousDocsForCurrentBizType, ...missingDocuments];
  };

  export const getDirectorId = (directorName, directorsList) => {
    if (directorsList) {
      const director = directorsList.find((doc) => doc.name === directorName)
      return director?.id
    }
  }